import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'
import { Check, Star } from 'lucide-react'

interface ConversationListItemProps {
  id: string
  visitorName: string
  visitorEmail?: string
  visitorAvatarUrl?: string
  subject?: string | null
  lastMessage: string
  lastMessageAt: string
  unreadCount?: number
  isResolved?: boolean
  isActive?: boolean
  assigneeName?: string | null
  csatRating?: number | null
  onClick?: () => void
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function ConversationListItem({
  visitorName,
  visitorEmail,
  visitorAvatarUrl,
  subject,
  lastMessage,
  lastMessageAt,
  unreadCount = 0,
  isResolved = false,
  isActive = false,
  assigneeName,
  csatRating,
  onClick,
}: ConversationListItemProps) {
  const hasUnread = unreadCount > 0

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 p-3 text-left rounded-lg transition-colors cursor-pointer',
        'hover:bg-sidebar-accent/50',
        isActive && 'bg-sidebar-accent',
        hasUnread && !isActive && 'bg-primary/5'
      )}
    >
      {/* Avatar */}
      <Avatar
        name={visitorName}
        email={visitorEmail}
        src={visitorAvatarUrl}
        avatarType={visitorAvatarUrl ? 'custom' : 'initials'}
        size="md"
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header row */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'font-medium truncate',
              hasUnread && 'text-foreground',
              !hasUnread && 'text-muted-foreground'
            )}
          >
            {visitorName}
          </span>
          <span className="text-xs text-muted-foreground shrink-0">
            {formatRelativeTime(lastMessageAt)}
          </span>
        </div>

        {/* Subject / assignee line */}
        {(subject || assigneeName) && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {subject}
            {subject && assigneeName && ' · '}
            {assigneeName && <span className="text-primary/70">{assigneeName}</span>}
          </p>
        )}

        {/* Message preview row */}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p
            className={cn(
              'text-sm truncate',
              hasUnread ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            {lastMessage}
          </p>

          {/* Status indicators */}
          <div className="flex items-center gap-1.5 shrink-0">
            {csatRating != null && (
              <span
                className="flex items-center gap-0.5 text-xs font-medium text-warning"
                title={`Visitor rated ${csatRating}/5`}
              >
                <Star className="size-3 fill-current" />
                {csatRating}
              </span>
            )}
            {isResolved && (
              <span className="flex items-center justify-center size-4 rounded-full bg-success/20">
                <Check className="size-3 text-success" />
              </span>
            )}
            {hasUnread && (
              <span className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

export default ConversationListItem
